/* eslint-disable react/no-multi-comp */

import React from 'react';
import warning from 'warning';
import {
  Button,
  Checkbox,
  CheckboxGroup,
  Fieldset,
  FormItem,
  Input,
  OptionGroup,
  RadioGroup,
  RangeSlider,
  Select,
  SwitchToggle,
  TextArea,
} from '@tesla/react-tds/global';
import isUndefined from 'lodash/isUndefined';
import withFormatted from './components/withFormatted';
import StatusMessage from './components/StatusMessage';
import cleanProps from './utils/cleanProps';

const hasSymbol = typeof Symbol === 'function' && Symbol.for;
const REACT_FORWARD_REF_TYPE = hasSymbol ? Symbol.for('react.forward_ref') : 0xead0;
const isForwardRef = (Component) =>
  typeof Component === 'object' && Component.$$typeof === REACT_FORWARD_REF_TYPE;
const isReactComponent = (Component) =>
  !!(
    typeof Component === 'function' &&
    Component.prototype &&
    Component.prototype.isReactComponent
  );
const canHaveRef = (Component) => isForwardRef(Component) || isReactComponent(Component);
const makeControlArray = (control) => {
  if (Array.isArray(control)) {
    return control;
  }

  return [control];
};
const isValidComponent = (component) =>
  React.isValidElement(component) || typeof component === 'function';
export const getComponentFromOptions = (options) => {
  if (isValidComponent(options)) {
    return options;
  }

  return options?.component;
};

const defaultComponentParams = {
  group: false,
  hasOptions: false,
  showLabel: true,
  weight: 0,
};

const Form = ({ children, error, ...props }) => (
  <form {...props}>
    {!!error && <StatusMessage>{error}</StatusMessage>}
    {children}
  </form>
);

const FormGroup = ({ children, title, ...props }) => {
  if (title) {
    return (
      <Fieldset title={title} {...props}>
        {children}
      </Fieldset>
    );
  }

  return children;
};

export default class TdsAdapter {
  componentMap = new Map([
    [
      'Input',
      {
        component: Input,
        control: ['string', 'integer', 'number'],
        transformProperties: (props) => props,
        wrapFormItem: true,
      },
    ],
    [
      'InputFormatted',
      {
        component: withFormatted(Input),
        control: ['formatted'],
        transformProperties: (props) => props,
        wrapFormItem: true,
      },
    ],
    [
      'Checkbox',
      {
        component: Checkbox,
        control: ['boolean', 'checkbox'],
        showLabel: false,
        transformProperties: (props, { trueValue }) => {
          const { value } = props;

          return {
            ...props,
            checked: value === trueValue,
          };
        },
        wrapFormItem: true,
      },
    ],
    [
      'CheckboxGroup',
      {
        component: CheckboxGroup,
        control: ['array', 'checkbox'],
        hasOptions: true,
        showLabel: false,
        transformProperties: (props) => props,
        wrapFormItem: true,
      },
    ],
    [
      'Select',
      {
        component: Select,
        control: ['string', 'integer', 'number', 'select'],
        hasOptions: true,
        transformProperties({ options, ...props }) {
          return {
            ...props,
            options: options.map((option) => {
              const { value } = option;

              if (value == null) {
                return {
                  ...option,
                  value: '',
                };
              }

              return option;
            }),
          };
        },
        wrapFormItem: true,
      },
    ],
    [
      'RadioGroup',
      {
        component: RadioGroup,
        control: ['radio'],
        hasOptions: true,
        showLabel: false,
        transformProperties: (props) => props,
        wrapFormItem: true,
      },
    ],
    [
      'RangeSlider',
      {
        component: RangeSlider,
        control: ['range'],
        showLabel: false,
        transformProperties: (props) => props,
        wrapFormItem: true,
      },
    ],
    [
      'TextArea',
      {
        component: TextArea,
        control: ['textarea'],
        transformProperties: (props) => props,
        wrapFormItem: true,
      },
    ],
    [
      'OptionGroup',
      {
        component: OptionGroup,
        control: ['option_group'],
        hasOptions: true,
        showLabel: false,
        transformProperties(props, { type }) {
          return {
            ...props,
            multiselect: type === 'array',
          };
        },
        wrapFormItem: true,
      },
    ],
    [
      'SwitchToggle',
      {
        component: SwitchToggle,
        control: ['switch_toggle'],
        hasOptions: true,
        transformProperties: (props, context) => ({
          ...props,
          falseLabel: context.falseLabel,
          trueLabel: context.trueLabel,
          trueValue: context.trueValue,
        }),
        wrapFormItem: true,
      },
    ],
    [
      'FormItem',
      {
        component: FormItem,
        transformProperties({ label, showLabel, showMask, ...props }, context) {
          const { $showError, error } = context;

          return {
            ...props,
            error: $showError,
            errorLabel: error || null,
            label: showLabel ? label : null,
          };
        },
      },
    ],
    [
      'Button',
      {
        component: Button,
        transformProperties: ({
          children,
          color,
          label = 'Submit',
          primary,
          type = 'submit',
          ...props
        }) => ({
          ...props,
          children: children != null ? children : label,
          color: primary ? 'blue' : color,
          type,
        }),
      },
    ],
    [
      'Form',
      {
        component: Form,
        transformProperties: (props, { error, $submitted }) => {
          if ($submitted) {
            return {
              error,
              ...props,
            };
          }

          return props;
        },
      },
    ],
    [
      'FormGroup',
      {
        component: FormGroup,
        transformProperties: (props) => props,
      },
    ],
    [
      'ArrayField',
      {
        component: React.Fragment,
        control: ['array'],
        transformProperties({ children }) {
          return { children };
        },
      },
    ],
  ]);

  constructor() {
    this.componentMap.forEach((options, componentName) => {
      const { transformProperties } = options;
      const component = getComponentFromOptions(options);
      const forwardComponent = this.getForwardComponent(component, transformProperties);

      this.componentMap.set(componentName, {
        ...options,
        component,
        forwardComponent,
      });
    });
  }

  get(componentName) {
    if (this.componentMap.has(componentName)) {
      const { forwardComponent } = this.componentMap.get(componentName);

      return forwardComponent;
    }

    if (!(componentName in this)) {
      warning(
        this[componentName],
        `The following component was not registered: ${componentName}. Using React Fragment instead`
      );

      return this.Fragment;
    }

    return this[componentName];
  }

  register(componentName, options) {
    let currentOptions = {};

    if (this.componentMap.has(componentName)) {
      currentOptions = this.componentMap.get(componentName);
    }

    let { component, forwardComponent } = currentOptions;
    const combinedOptions = { ...currentOptions, ...options };

    if (isValidComponent(options) || !isUndefined(options.component)) {
      const { transformProperties } = options;
      component = getComponentFromOptions(options);
      forwardComponent = this.getForwardComponent(component, transformProperties);
    }

    this.componentMap.set(componentName, {
      ...combinedOptions,
      component,
      forwardComponent,
    });
  }

  findComponent = (control, hasOptions) => {
    const componentMap = [...this.componentMap]
      .map(([key, inputParams]) => ({
        ...defaultComponentParams,
        ...inputParams,
        key,
      }))
      .sort(({ weight: weightA }, { weight: weightB }) => {
        if (weightA < weightB) return -1;
        if (weightA > weightB) return 1;
        return 0;
      });

    return componentMap.find(({ control: componentControl, hasOptions: componentHasOptions }) =>
      makeControlArray(componentControl).some((individualControl) => {
        let individualControlName = individualControl;
        let individualControlHasOptions = componentHasOptions;

        if (Array.isArray(individualControl)) {
          [individualControlName, individualControlHasOptions] = individualControl;
        }

        return individualControlName === control && individualControlHasOptions === hasOptions;
      })
    );
  };

  transformComponent = (
    Component,
    transformProperties,
    {
      context = {},
      defaultHandler,
      formApi,
      formFieldApi,
      formFieldState,
      formState,
      ref,
      userProps,
      ...props
    }
  ) => {
    if (Component == null) return null;

    const transformedProps =
      typeof transformProperties === 'function'
        ? transformProperties(props, context)
        : cleanProps({
            children: props.children,
            defaultHandler,
            formApi,
            formFieldApi,
            formFieldState,
            formState,
            userProps,
          });

    if (canHaveRef(Component)) {
      transformedProps.ref = ref;
    }

    const TransformedComponent = React.createElement(Component, transformedProps);

    return TransformedComponent;
  };

  getForwardComponent = (Component, transformProperties) =>
    React.forwardRef((props, ref) =>
      // eslint-disable-next-line react/no-this-in-sfc
      this.transformComponent(Component, transformProperties, {
        ...props,
        ref,
      })
    );

  FormFieldComponent = ({
    children,
    context,
    defaultHandler,
    forwardRef,
    inputProps,
    userProps,
    formApi,
    formFieldApi,
    formFieldState,
    formState,
    ...props
  }) => {
    const { options } = props;
    const hasOptions = Array.isArray(options);
    const { control } = context;
    const { forwardComponent: TdsAdapterComponent, showLabel, wrapFormItem = false } =
      this.findComponent(control, hasOptions) || {};
    const { forwardComponent: TdsAdapterFormItem } = this.componentMap.get('FormItem') || {};

    if (TdsAdapterFormItem == null) {
      return null;
    }

    warning(TdsAdapterComponent, `Not implemented: ${control}`);

    const controlComponent = TdsAdapterComponent ? (
      <TdsAdapterComponent
        {...inputProps}
        context={context}
        defaultHandler={defaultHandler}
        formApi={formApi}
        formFieldApi={formFieldApi}
        formFieldState={formFieldState}
        formState={formState}
        ref={forwardRef}
        userProps={userProps}
      >
        {children}
      </TdsAdapterComponent>
    ) : (
      this.Fragment({ children, ...inputProps })
    );

    if (wrapFormItem) {
      return (
        <TdsAdapterFormItem {...props} context={context} showLabel={showLabel}>
          {controlComponent}
        </TdsAdapterFormItem>
      );
    }

    return controlComponent;
  };

  FormField = React.forwardRef((props, ref) => {
    const { FormFieldComponent } = this;

    return <FormFieldComponent {...props} forwardRef={ref} />;
  });

  Fragment = ({ children }) => <React.Fragment>{children}</React.Fragment>;
}
